/**
 * "The page the member asked for has its data." A one-way latch plus subscribers,
 * used to hold speculative work until the hot path is genuinely done (IN-46 Phase 4).
 *
 * WHY NOT `requestIdleCallback` ALONE — this is the whole point of the file.
 * A cold start is network-bound, not CPU-bound: the main thread sits idle for the
 * ~455 ms of `/auth/refresh` and again for the dashboard's own queries. So the
 * browser is "idle" in exactly the window we are trying to keep clear, and an
 * idle callback fires straight into it. Measured: deferring the Chat chunk with
 * `requestIdleCallback` alone left it loading BEFORE the dashboard cards appeared,
 * i.e. it deferred nothing at all.
 *
 * The reliable signal is the application one — the page's own data has landed —
 * and idle is then a refinement on top of it, not a substitute for it.
 *
 * Deliberately module-scoped rather than context: the producer (a dashboard page)
 * and the consumers (layout chrome, background polls) are far apart in the tree and
 * neither should have to thread a provider through to reach the other.
 */
type Listener = () => void

let done = false
let listeners: Listener[] = []

/** Called by the first page whose primary data has arrived. Idempotent. */
export function markFirstPaintDone(): void {
  if (done) return
  done = true
  const queued = listeners
  listeners = []
  for (const fn of queued) fn()
}

export function isFirstPaintDone(): boolean {
  return done
}

/**
 * Run `fn` once the hot path is done — immediately if it already is. Returns a
 * cancel function so an effect that unmounts first does not fire it.
 */
export function whenFirstPaintDone(fn: Listener): () => void {
  if (done) {
    fn()
    return () => {}
  }
  listeners.push(fn)
  return () => {
    listeners = listeners.filter(l => l !== fn)
  }
}

/** Tests only: forget the latch so each case starts cold. */
export function resetFirstPaintSignalForTests(): void {
  done = false
  listeners = []
}
